import { useNavigate } from 'react-router-dom'
import { X } from 'lucide-react'
import { CONTENT_CONTAINER } from '../../lib/layout'
import { track } from '../../lib/track'
import {
  THEMES,
  isPaidProfile,
  previewedTheme,
  shouldShowPreviewBar,
  useThemeStore,
} from '../../lib/theme'
import { useFlagStore } from '../../hooks/useFeatureFlags'
import { useUserStore } from '../../stores/userStore'

/**
 * Floating preview / upgrade bar for the batch-3 theme picker.
 *
 * Mounted once in the app shell (next to BottomNav) so it hovers above the tab
 * bar on EVERY screen while a free viewer is trying on a paid palette. Its
 * visibility is a pure derivation of the theme store — {@link shouldShowPreviewBar}
 * — so it appears the instant a preview is set and disappears when it's cleared
 * (or when an effectively-paid viewer no longer needs it). Paid users never see
 * it.
 */
export function ThemePreviewBar() {
  const navigate = useNavigate()
  const themeEnabled = useFlagStore((s) => !!s.flags['theme_picker'])
  const profile = useUserStore((s) => s.profile)
  const preview = useThemeStore((s) => s.preview)
  const stored = useThemeStore((s) => s.stored)
  // Subscribed, not read through getActiveForcedTheme(): the module getter is
  // not reactive, so the bar would keep its stale visibility when a forced
  // surface mounts or unmounts. setActiveForcedTheme mirrors into this field
  // for exactly this reason.
  const forced = useThemeStore((s) => s.forcedTheme)
  const setPreview = useThemeStore((s) => s.setPreview)

  // Flag-off must be byte-identical to batch-2: even a stale sessionStorage
  // preview from a prior flag-on session must not surface the bar.
  if (!themeEnabled) return null

  const isPaid = isPaidProfile(profile)
  if (!shouldShowPreviewBar({ isPaid, preview, stored, forced })) return null

  const themeId = previewedTheme({ preview, stored })
  const theme = THEMES.find((t) => t.id === themeId)
  if (!theme) return null

  const handleUpgrade = () => {
    track('upgrade_clicked', { from: 'preview_bar' })
    navigate('/upgrade?from=preview_bar')
  }

  return (
    <div className="shrink-0 pointer-events-none">
      <div className={`${CONTENT_CONTAINER} px-3`}>
        <div className="pointer-events-auto -mb-1 flex items-center gap-2.5 rounded-xl border border-accent-border bg-bg-card px-3 py-2.5 shadow-[var(--shadow-flat)]">
          <span
            className="h-3.5 w-3.5 shrink-0 rounded-full"
            style={{ background: theme.swatch.accent }}
            aria-hidden="true"
          />
          <span className="min-w-0 flex-1 font-body text-[0.76rem] text-text-primary">
            Previewing <strong className="font-medium">{theme.label}</strong> — keep it with Cosmic
          </span>
          <button
            onClick={handleUpgrade}
            className="shrink-0 rounded-lg border border-accent-border bg-accent-bg px-2.5 py-1.5 font-body text-[0.7rem] font-medium text-accent-fg transition-transform active:scale-[0.96]"
          >
            Upgrade ✦
          </button>
          <button
            onClick={() => setPreview(null)}
            aria-label="Stop previewing"
            className="flex shrink-0 rounded-lg p-1.5 text-text-muted transition-transform active:scale-[0.96] hover:text-text-primary"
          >
            <X size={14} />
          </button>
        </div>
      </div>
    </div>
  )
}
